import { CHANNEL_ID, LOG_ID, THREAD_CHANNEL_ID } from '@@types/common';
import FolderFavorite from '@@types/FolderFavorite';
import Log, { AllThreadLog } from '@@types/Log';

import {
  CallerMenu,
  CHANNEL_MENU,
  ChannelSettingMemberMenu,
  CreateChannelMenu,
  FavoriteFolderMenu,
  IpPhoneStatus,
  LOG_MENU,
  MAIN_THREAD_CHANNEL_ID,
  MemberStatus,
  MODAL_NAMES,
  NEW_LINE_MANNER,
  NewLineMenu,
  NOTICE_MENU,
  PANEL_TYPE,
  Placement,
  PopperType,
  PROFILE_TYPE,
  THREAD_MENU,
  UserMenu,
} from '~/constants';
import { HELP_PAGE_ID } from '~/constants/helpPage';
import { Place } from '~/contexts/Channel/ChannelContext';
import i18n from '~/i18n';
import GA from '~/services/GA/GA';
import popupWindowService from '~/services/popupWindowService';
import { channelLogsStore } from '~/stores/ChannelLogsStore';
import { channelManager } from '~/stores/ChannelManager';
import { channelsStore } from '~/stores/ChannelsStore';
import { mainDetailUiState, threadDetailUiState } from '~/stores/DetailUiState';
import { favoritesStore } from '~/stores/FavoritesStore';
import { forwardLogStore } from '~/stores/ForwardLogStore';
import { globalSettingStore } from '~/stores/GlobalSettingStore';
import { api } from '~/stores/providers/Api2';
import { replyLogStore } from '~/stores/ReplyLogStore';
import { threadChannelStore } from '~/stores/ThreadChannelStore';
import { uiState } from '~/stores/UiState';
import { userStore } from '~/stores/UserStore';
import { getSenderId } from '~/utils/channelLogsUtil';
import { getHelpUrl } from '~/utils/helpUrlUtil';
import { cond, curry, equals, startsWith } from 'ramda';
import { IO, Maybe } from 'ramda-fantasy';

import Actions from '../Actions';
import { stores } from '../Stores';

class MenuActions {
  private handleChannelProfile = curry((referenceNode: HTMLElement, value: string) => {
    const [, channelId, memberId] = value.split(':');
    uiState.openPopper({
      name: PopperType.Profile,
      args: {
        referenceNode,
        memberId,
        channelId,
        type: PROFILE_TYPE.MEMBER,
        options: {
          placement: Placement.RightStart,
          modifiers: [
            { name: 'offset', options: { offset: [-10, 5] } },
            { name: 'flip', options: { fallbackPlacements: ['right', 'left', 'bottom'] } },
          ],
        },
      },
    });
  });

  private handleChannelSettingMemberProfile = curry((referenceNode: HTMLElement, value: string) => {
    const [, channelId, memberId] = value.split(':');
    uiState.openPopper({
      name: PopperType.Profile,
      args: {
        referenceNode,
        memberId,
        channelId,
        type: PROFILE_TYPE.MEMBER,
        options: {
          placement: Placement.LeftStart,
          modifiers: [
            { name: 'offset', options: { offset: [0, 10] } },
            { name: 'flip', options: { fallbackPlacements: ['right', 'left', 'bottom'] } },
          ],
        },
      },
    });
  });

  constructor(private actions: Actions) {}

  get locale(): IO<string> {
    return IO(() => stores.locale);
  }

  public canEditOrDeleteLog = (log: Log) => {
    const channel = channelManager.getById(log.channelId);
    const isReadonly = this.actions.permissionActions.isReadonlyChannel(channel);

    const { canDeleteMessage } = globalSettingStore.messengerGeneralSetting;
    const isUserLog = getSenderId(log) === userStore.id;
    return !isReadonly && canDeleteMessage && isUserLog;
  };

  handleLogMenu = ({
    value,
    logId,
    channelId,
    log,
    startLog,
    place,
    emoticon,
    inThread,
  }: {
    value: LOG_MENU;
    channelId: CHANNEL_ID;
    logId: LOG_ID;
    log: Log;
    startLog: boolean;
    place: Place;
    emoticon: string;
    inThread: boolean;
  }) => {
    switch (value) {
      case LOG_MENU.OK:
      case LOG_MENU.HEARTS:
      case LOG_MENU.THUMB_UP:
      case LOG_MENU.HEAVY_CHECK_MARK:
        const params = {
          logId,
          channelId,
          emoticon,
        };
        if (inThread) {
          this.actions.threadLogActions.toggleEmoticon(params);
        } else {
          this.actions.logActions.toggleEmoticon(params);
        }
        return;
      case LOG_MENU.NOTICE:
        return this.handleLogNotice(logId, log);
      case LOG_MENU.EDIT:
        return this.handleEditLog(logId, startLog, place);
      case LOG_MENU.DELETE:
        return this.handleRemoveLog(logId);
      case LOG_MENU.FORWARD:
        return this.handleForwardLog(logId);
      case LOG_MENU.REPLY:
        return this.handleReplyLog(logId, log);
      case LOG_MENU.THREAD:
        return this.handleThread(log as AllThreadLog);
    }
  };

  handleChannelMenu = (value: string, referenceNode?: HTMLElement) => {
    cond([
      [startsWith(CHANNEL_MENU.POPUP), this.handleChannelPopup],
      [startsWith(CHANNEL_MENU.FAVORITE_ON), this.handleChannelFavorite],
      [startsWith(CHANNEL_MENU.FAVORITE_OFF), this.handleChannelFavorite],
      [startsWith(CHANNEL_MENU.SETTING), this.handleChannelSetting],
      [startsWith(CHANNEL_MENU.PROFILE), this.handleChannelProfile(referenceNode)],
      [startsWith(CHANNEL_MENU.NOTIFICATION_ON), this.handleChannelNotification],
      [startsWith(CHANNEL_MENU.NOTIFICATION_OFF), this.handleChannelNotification],
      [startsWith(CHANNEL_MENU.DISPLAY_ON), this.handleChannelDisplay],
      [startsWith(CHANNEL_MENU.DISPLAY_OFF), this.handleChannelDisplay],
      [startsWith(CHANNEL_MENU.LEAVE), this.handleChannelLeave],
    ])(value);
  };

  handleNewLineMenu = (newLine: NewLineMenu) => {
    if (newLine === NewLineMenu.ShiftEnter) {
      this.actions.settingActions.setNewLine(NEW_LINE_MANNER.SHIFT_ENTER);
    } else {
      this.actions.settingActions.setNewLine(NEW_LINE_MANNER.ENTER);
    }
  };

  handleChannelSettingMemberMenu = (value: string, referenceNode?: HTMLElement) => {
    cond([
      [startsWith(ChannelSettingMemberMenu.Chat), this.handleChannelSettingmemberChat],
      [startsWith(ChannelSettingMemberMenu.Profile), this.handleChannelSettingMemberProfile(referenceNode)],
      [startsWith(ChannelSettingMemberMenu.Leave), this.handleChannelSettingmemberLeave],
      [startsWith(ChannelSettingMemberMenu.Kick), this.handleChannelSettingMemberKick],
      [startsWith(ChannelSettingMemberMenu.AddAdmin), this.handleChannelSettingmemberAddAdmin],
      [startsWith(ChannelSettingMemberMenu.DeleteAdmin), this.handleChannelSettingmemberDeleteAdmin],
    ])(value);
  };

  public handleThreadMenu = (channelId: THREAD_CHANNEL_ID, value: THREAD_MENU) => {
    switch (value) {
      case THREAD_MENU.FOLLOW:
        return this.follow(channelId);
      case THREAD_MENU.UNFOLLOW:
        return this.unfollow(channelId);
      case THREAD_MENU.EXPORT_TASK:
        return this.exportTask(channelId);
      case THREAD_MENU.EXPORT_TASK_WITH_AI:
        return this.exportTask(channelId, true);
    }
  };

  private follow = async (channelId: THREAD_CHANNEL_ID) => {
    await api.channel.inviteChannelMembers(channelId, [userStore.id]);
  };

  private unfollow = async (channelId: THREAD_CHANNEL_ID) => {
    await api.channel.leaveChannel(channelId);
    if (mainDetailUiState.contentsChannelId === MAIN_THREAD_CHANNEL_ID) {
      threadChannelStore.removeThreadChannelInMainThreadChannel(channelId);
    }
  };

  private exportTask = async (channelId: THREAD_CHANNEL_ID, withAI = false) => {
    GA.writeThread({ action: '채널', label: withAI ? '업무 등록 with AI' : '업무 등록' });
    if (withAI) {
      this.actions.modalActions.openAlert({
        description: i18n.t('thread.exportTaskWithAIMessage', {
          defaultValue: 'AI를 활용하여 대화 분석 중입니다...',
        }),
        hideClose: true,
      });
    }
    try {
      const result = await api.channel.exportThreadChannelToTask(channelId, withAI);
      if (result.overLimit) {
        this.actions.modalActions.openAlert({
          description: i18n.t('thread.exportTask.overLimitAlert', {
            defaultValue:
              '댓글 {{limit}}개를 초과하는 경우, 댓글을 제외한 나머지 내용만 업무 내보내기 됩니다. (스레드 첫 메시지, 댓글 작성자, 파일, 이미지)',
            limit: result.limit,
          }),
        });
      }
      popupWindowService.openTaskDraft(result.draftId);
    } finally {
      uiState.closeModal(MODAL_NAMES.ALERT);
    }
  };

  private openModal = (params) => {
    uiState.openModal(params);
  };

  private openPopup = (url: string) => {
    popupWindowService.open(url, '_blank', { width: 1280 });
  };

  private handleStatus = (status: string) => {
    this.actions.proxyActions.updateMeStatus(status as MemberStatus);
  };

  private handleProfile = () => {
    this.openModal({ name: MODAL_NAMES.PROFILE, args: { channelId: stores.doorayNewsChannelId } });
  };

  private handleGlobalSetting = () => {
    this.openModal({ name: MODAL_NAMES.GLOBAL_SETTING });
  };

  private handleManageMembers = () => {
    this.openPopup(`/org/department-member?orgFilter=!${userStore.orgId}`);
  };

  private handleInviteMembers = () => {
    this.actions.modalActions.openTenantMembersInvite();
  };

  private handleCommandSetting = () => {
    this.openPopup(`/org/tenant-default-command?orgFilter=!${userStore.orgId}`);
  };

  private handleSlashCommand = () => {
    this.actions.openSlashCommandIntegrationPopup();
  };

  private handleManageHiddenChannel = () => {
    this.actions.modalActions.openManageHiddenChannel();
  };

  private handleRelease = () => {
    this.openPopup(
      getHelpUrl({
        helpUrl: globalSettingStore.env.helpUrl,
        locale: globalSettingStore.env.locale,
        pageId: HELP_PAGE_ID['3475524696812012392'],
      })
    );
  };

  private handleDownload = () => {
    this.openPopup(`${globalSettingStore.env.doorayDotComUrl}/main/download/`);
  };

  private handleInquire = () => {
    const inquireURL = this.actions.memberActions.getInquireUrl();

    if (!inquireURL) {
      const description = i18n.t('inquireAlert.description', '{{helpMailDomain}}으로 문의해 주세요.', {
        helpMailDomain: stores.helpMailDomain,
      });
      this.actions.modalActions.openAlert({ description });
      return;
    }

    popupWindowService.open(inquireURL, '_blank', {
      width: 1024,
      height: 800,
    });
  };

  private handleLogout = () => {
    this.actions.userActions.logout();
  };

  handleUserMenu = cond([
    [equals(UserMenu.Active), this.handleStatus],
    [equals(UserMenu.Busy), this.handleStatus],
    [equals(UserMenu.Away), this.handleStatus],
    [equals(UserMenu.Profile), this.handleProfile],
    [equals(UserMenu.GlobalSetting), this.handleGlobalSetting],
    [equals(UserMenu.ManageMembers), this.handleManageMembers],
    [equals(UserMenu.InviteMembers), this.handleInviteMembers],
    [equals(UserMenu.CommandSetting), this.handleCommandSetting],
    [equals(UserMenu.SlashCommand), this.handleSlashCommand],
    [equals(UserMenu.ManageHiddenChannel), this.handleManageHiddenChannel],
    [equals(UserMenu.Release), this.handleRelease],
    [equals(UserMenu.Download), this.handleDownload],
    [equals(UserMenu.Inquire), this.handleInquire],
    [equals(UserMenu.Logout), this.handleLogout],
  ]);

  private handleLogNotice = (logId: LOG_ID, log: Log) => {
    api.notice.postChannelNotice(log.channelId, logId);
  };

  private handleEditLog = (logId: LOG_ID, startLog: boolean, place: Place) => {
    if (startLog) {
      threadDetailUiState.setEditLog(logId, place);
      return;
    }
    channelLogsStore.getMainLogById(logId)
      ? mainDetailUiState.setEditLog(logId, place)
      : threadDetailUiState.setEditLog(logId, place);
  };

  private handleRemoveLog = (logId: LOG_ID) => {
    uiState.openModal({
      name: MODAL_NAMES.REMOVE_LOG,
      args: { logId },
    });
  };

  private handleForwardLog = (logId: LOG_ID) => {
    uiState.openModal({
      name: MODAL_NAMES.LOG_FORWARD_CHANNEL_SELECT,
      args: { logId },
    });
  };

  private handleReplyLog = (logId: LOG_ID, log: Log) => {
    const inThread = !channelLogsStore.getMainLogById(logId);
    const detailUiState = inThread ? threadDetailUiState : mainDetailUiState;
    const isScrollBottom = detailUiState.scrollAreaInfo.isScrollBottom;
    if (!inThread) {
      forwardLogStore.removeForwardLog(detailUiState.contentsChannelId);
    }
    replyLogStore.setReplyLog(log);
    uiState.closePopper(PopperType.LogMenu);
    inThread ? this.actions.threadMainEditorActions.focus() : this.actions.mainEditorActions.focus();
    isScrollBottom && detailUiState.scrollToBottom();
  };

  private handleThread = (log: AllThreadLog) => {
    uiState.setPanelType(PANEL_TYPE.NONE);
    this.actions.threadChannelActions.safeOpenThreadChannel(log);
  };

  private handleChannelPopup = (value: string) => {
    const [, channelId] = value.split(':');
    this.actions.openPopupChannel(channelId);
    if (channelId === MAIN_THREAD_CHANNEL_ID) {
      uiState.setHasUnreadThread(false);
    }
  };

  private handleChannelFavorite = (value: string) => {
    const [, channelId] = value.split(':');
    this.actions.favoriteActions.toggleChannel(channelId);
  };

  private handleChannelSetting = (value: string) => {
    const [, channelId] = value.split(':');
    this.actions.modalActions.openChannelProfileSetting({ channelId });
  };

  private handleChannelNotification = (value: string) => {
    const [, channelId] = value.split(':');
    this.actions.channelActions.toggleNotification(channelId);
  };

  private handleChannelDisplay = (value: string) => {
    const [, channelId] = value.split(':');
    const channel = channelsStore.getById(channelId);

    if (channel.displayed) {
      this.actions.modalActions.openChannelHide(channelId);
    } else {
      this.actions.channelActions.toggleDisplay(channelId);
    }
  };

  private handleChannelLeave = (value: string) => {
    const [, channelId] = value.split(':');
    this.actions.modalActions.openChannelLeave(channelId);
  };

  private handleNoticeDelete = () => {
    api.notice.deletePostNotice(mainDetailUiState.contentsChannelId);
  };

  private handleNoticeShowList = () => {
    uiState.setPanelType(PANEL_TYPE.NOTICE);
  };

  handleNoticeMenu = cond<any, any>([
    [startsWith(NOTICE_MENU.DELETE), this.handleNoticeDelete],
    [startsWith(NOTICE_MENU.SHOW_LIST), this.handleNoticeShowList],
  ]);

  private handleCreateChannelDirect = () => {
    GA.writeGnbMenu(uiState.gnbTab, '인스턴스 대화방 추가');
    this.actions.modalActions.openDirectChannelCreate();
  };

  private handleCreateChannelPrivate = () => {
    GA.writeGnbMenu(uiState.gnbTab, '주제 대화방 추가');
    this.actions.modalActions.openTopicChannelCreate();
  };

  handleCreateChannelMenu = cond<[string], void>([
    [startsWith(CreateChannelMenu.Direct), this.handleCreateChannelDirect],
    [startsWith(CreateChannelMenu.Private), this.handleCreateChannelPrivate],
  ]);

  private handleFavoriteFolderTitle = (value: string) => {
    const [, folderId] = value.split(':');
    const folderMaybe = favoritesStore.safeGetFolderById(folderId);
    folderMaybe.map((folder: FolderFavorite) => {
      this.actions.modalActions.openFavoriteFolderEdit({ folder });
    });
  };

  private handleFavoriteFolderRemove = (value: string) => {
    const [, folderId] = value.split(':');
    this.actions.modalActions.openFavoriteFolderRemove(folderId);
  };

  handleFavoriteFolderMenu = cond([
    [startsWith(FavoriteFolderMenu.Title), this.handleFavoriteFolderTitle],
    [startsWith(FavoriteFolderMenu.Remove), this.handleFavoriteFolderRemove],
  ]);

  private handleChannelSettingmemberChat = (value: string) => {
    const [, memberId] = value.split(':');
    this.actions.channelActions.selectOrCreateDirectChannelByOpponentId(memberId);
  };

  private handleChannelSettingmemberLeave = () => {
    this.actions.modalActions.openChannelLeave(mainDetailUiState.contentsChannelId);
  };

  private handleChannelSettingMemberKick = (value: string) => {
    const [, memberId] = value.split(':');
    this.actions.proxyActions.removeChannelMembers(mainDetailUiState.contentsChannelId, [memberId]);
  };

  private handleChannelSettingmemberAddAdmin = (value: string) => {
    const [, channelId, memberId] = value.split(':');
    api.channel.updateChannelMemberRole({ channelId, memberId, role: 'admin' });
  };

  private handleChannelSettingmemberDeleteAdmin = (value: string) => {
    const [, channelId, memberId] = value.split(':');

    const isAdminMode = this.actions.channelActions.checkAdminMode(channelId);
    if (isAdminMode && channelsStore.isOnlyAdmin(channelId, memberId)) {
      const description = i18n.t('userSettingMembers.memberMenu.deleteAdminAlert');
      this.actions.modalActions.openAlert({ description });
      return;
    }

    api.channel.updateChannelMemberRole({ channelId, memberId, role: 'member' });
  };

  private isAvailableIpPhone = (status: IpPhoneStatus) => {
    switch (status) {
      case 'UNKNOWN':
      case 'IDLE':
        return Maybe.Just(status);
      case 'ACTIVE':
      case 'ALERTING':
      default:
        return Maybe.Nothing();
    }
  };

  private handleCallerIpPhone = async (value: string) => {
    const [, calleePhoneNumber] = value.split(':');
    GA.writeClick({ action: '프로필 영역 > 휴대폰 영역', label: '사내전화로 통화' });
    const { status } = await api.phone.fetchIpPhoneStatus(calleePhoneNumber);
    this.isAvailableIpPhone(status).map(() => {
      api.phone.executeIpPhoneCall(calleePhoneNumber);
    });
  };

  private handleCallerCellPhone = async (value: string) => {
    const [, calleeName, calleePhoneNumber] = value.split(':');
    GA.writeClick({ action: '프로필 영역 > 휴대폰 영역', label: '휴대폰으로 통화' });
    await this.actions.modalActions.openConfirm({
      description: i18n.t('callWithUserPhone.description'),
    });
    api.phone.executeCall(userStore.id, { calleeName, calleePhoneNumber });
  };

  handleCallerMenu = cond([
    [startsWith(CallerMenu.IpPhone), this.handleCallerIpPhone],
    [startsWith(CallerMenu.CellPhone), this.handleCallerCellPhone],
  ]);
}

export default MenuActions;
